/**
 * App Tour videos. The files live outside the app, under `videoBaseUrl`
 * (GET /tour/state):
 *
 *   ${videoBaseUrl}/manifest.json  →  { "videos": ["web/M08-L20-hi.mp4", "android/M01-L01-en.mp4"] }
 *   ${videoBaseUrl}/${platform}/${lessonId}-${lang}.mp4
 *
 * A lesson whose file is not in the manifest has no video ("Video coming soon").
 */
export type TourVideoPlatform = 'web' | 'android';
export type TourVideoLanguage = 'hi' | 'en';

export interface TourVideoChoice {
  url: string;
  /** The language that plays: the one asked for, or the only one that exists. */
  language: TourVideoLanguage;
  has: Record<TourVideoLanguage, boolean>;
}

/** The manifest's file list. Any failure → no videos. Sent without cookies: it is another host. */
export async function fetchVideoManifest(videoBaseUrl: string | null): Promise<string[]> {
  if (!videoBaseUrl) return [];
  try {
    const response = await fetch(`${videoBaseUrl}/manifest.json`, { credentials: 'omit' });
    if (!response.ok) return [];
    const body: unknown = await response.json();
    const videos = (body as { videos?: unknown } | null)?.videos;
    return Array.isArray(videos) ? videos.filter((file): file is string => typeof file === 'string') : [];
  } catch { return []; }
}

export function pickVideo(
  videos: string[], videoBaseUrl: string | null, platform: TourVideoPlatform, lessonId: string, wanted: TourVideoLanguage,
): TourVideoChoice | null {
  if (!videoBaseUrl) return null;
  const file = (language: TourVideoLanguage) => `${platform}/${lessonId}-${language}.mp4`;
  const has = { hi: videos.includes(file('hi')), en: videos.includes(file('en')) };
  if (!has.hi && !has.en) return null;
  const language = has[wanted] ? wanted : wanted === 'hi' ? 'en' : 'hi';
  return { url: `${videoBaseUrl}/${file(language)}`, language, has };
}
